/**
 * Modal dialogs for the GRN Allocation Repair page.
 *
 * Extracted from ``GrnAllocationRepair.tsx`` so that file stays under the
 * project's 500-line ceiling.
 *
 *  - ``SplitModal`` — chip a quantity off one allocation into a sibling row.
 *  - ``CancelScheduleModal`` — confirm retiring a schedule line and
 *    transferring its GRNs.
 *  - ``QtyEditModal`` — correct a schedule line's demanded quantity.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { fmtDate } from './GrnRepairCards';

export function SplitModal({ grnId, currentQty, splitQty, onQtyChange, onClose, onSplit }: {
  grnId: number;
  currentQty: string;
  splitQty: string;
  onQtyChange: (v: string) => void;
  onClose: () => void;
  onSplit: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-md shadow-xl p-5 w-[360px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-semibold mb-1">Split GRN #{grnId}</div>
        <div className="text-xs text-muted-foreground mb-3">
          Currently {currentQty} on this slot. Enter the quantity to chip off into a new
          allocation row (it will start on the same schedule; drag it elsewhere after splitting).
        </div>
        <Input
          type="number"
          autoFocus
          value={splitQty}
          onChange={(e) => onQtyChange(e.target.value)}
          placeholder={`< ${currentQty}`}
          className="text-sm"
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={onSplit}>Split</Button>
        </div>
      </div>
    </div>
  );
}

export function CancelScheduleModal({ lineNumber, schedDate, cancelling, onClose, onConfirm }: {
  lineNumber: string;
  schedDate: string | null;
  cancelling: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={() => !cancelling && onClose()}
    >
      <div
        className="bg-white rounded-md shadow-xl p-5 w-[420px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-semibold mb-1">
          Cancel schedule line {lineNumber} ({fmtDate(schedDate)})?
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          This retires the slot and transfers its GRNs to the line's active schedule
          (or the nearest remaining schedule; if none remain, they're left unallocated).
          The cancelled line is excluded from all future auto-attribution. You can
          reverse this later with "Un-cancel" on the retired line.
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={cancelling} onClick={onClose}>Keep it</Button>
          <Button variant="destructive" size="sm" disabled={cancelling} onClick={onConfirm}>
            {cancelling && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Cancel line
          </Button>
        </div>
      </div>
    </div>
  );
}

export function UncancelScheduleModal({ lineNumber, schedDate, uncancelling, onClose, onConfirm }: {
  lineNumber: string;
  schedDate: string | null;
  uncancelling: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={() => !uncancelling && onClose()}
    >
      <div
        className="bg-white rounded-md shadow-xl p-5 w-[420px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-semibold mb-1">
          Un-cancel schedule line {lineNumber} ({fmtDate(schedDate)})?
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          This reactivates the retired slot and restores its outstanding demand
          (balance recomputed from the schedule quantity). GRNs transferred off at
          cancel time are NOT pulled back — the slot reopens empty and can be
          re-allocated. It re-enters auto-attribution and the open/arrears views.
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={uncancelling} onClick={onClose}>Keep cancelled</Button>
          <Button size="sm" disabled={uncancelling} onClick={onConfirm}>
            {uncancelling && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Un-cancel line
          </Button>
        </div>
      </div>
    </div>
  );
}

export function QtyEditModal({ lineNumber, schedDate, currentQty, saving, onClose, onSave }: {
  lineNumber: string;
  schedDate: string | null;
  currentQty: string;
  saving: boolean;
  onClose: () => void;
  onSave: (qty: string) => void;
}) {
  const [qty, setQty] = useState(currentQty);
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={() => !saving && onClose()}
    >
      <div
        className="bg-white rounded-md shadow-xl p-5 w-[380px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-semibold mb-1">
          Edit quantity — line {lineNumber} ({fmtDate(schedDate)})
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          Currently {currentQty}. Enter the corrected demanded quantity. Balance,
          over-delivery flags, and the active pointer recompute automatically.
        </div>
        <Input
          type="number"
          autoFocus
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="text-sm"
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={saving} onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={saving} onClick={() => onSave(qty)}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Save qty
          </Button>
        </div>
      </div>
    </div>
  );
}
